import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Plus, Minus, Send } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

const FAQ = [
  {
    q: "What do I get with a membership?",
    a: "Access to your chosen private trading room (Crypto, Stocks or Forex) or all three with All Access. That means live trading ideas, market breakdowns, memecoin plays, session analysis, mentor Q&As, weekly outlooks, and access to the founder Issy Flash.",
  },
  {
    q: "How is access delivered after I pay?",
    a: "Instantly. Payment goes through Whop — the moment your card clears you're auto-added to your paid Telegram group. You'll only ever get access to the plan you paid for. Nothing more, nothing less.",
  },
  {
    q: "Walk me through the full joining process — from clicking Join to being inside the Telegram group.",
    a: "1) On our Pricing page, tap JOIN on the plan you want (Crypto, Stocks, Forex or All Access). 2) A confirmation card opens showing your plan, price and billing cycle — tap PAY WITH WHOP to be taken to Whop's secure checkout. 3) On Whop, enter your email and pay with card or Apple Pay (Whop handles the payment — we never see your card details). 4) The moment payment clears, Whop emails you a receipt and — most importantly — a unique Telegram invite link tied to your account. 5) Open that email on your phone, tap the Telegram invite link, and Telegram will open with a Join Group button. 6) Tap Join and you're inside the private room straight away — live trades, mentor Q&A and market flow already waiting for you. If you don't see the email within a minute, check spam or your Whop dashboard at whop.com/orders — the same invite link lives there permanently.",
  },
  {
    q: "Which payment methods do you accept?",
    a: "Card (Visa, Mastercard, Amex) and Apple Pay via Whop's secure checkout. Whop handles the payment processing — we never see your card details.",
  },
  {
    q: "Can I switch between plans (e.g. Crypto → All Access)?",
    a: "Yes. You can cancel your current plan inside your Whop account and subscribe to a different one at any time. If you'd like help with the upgrade, DM @issyflash on Telegram.",
  },
  {
    q: "Do you offer refunds?",
    a: "Because access is granted instantly at time of payment, all sales are final. If something is wrong with your access, DM @issyflash and we'll sort it out same-day.",
  },
  {
    q: "How do I cancel my subscription?",
    a: "Log into your Whop account → Subscriptions → Cancel. You keep access until the end of your paid billing period. No hoops, no phone calls.",
  },
  {
    q: "What is SynDex?",
    a: "SynDex is our premium AI trading bot. It scans crypto markets 24/7 and delivers high-conviction trading ideas straight to All Access and Crypto members. Included in those plans at no extra cost.",
  },
  {
    q: "Is what you share financial advice?",
    a: "No. Everything shared inside The Syndicate is educational content and the personal opinions of members and mentors. You are 100% responsible for your own trades. Never risk money you can't afford to lose.",
  },
  {
    q: "Who runs The Syndicate?",
    a: "Founding partner Issy Flash — six years in the crypto markets, a six-figure memecoin year in 2025, and a track record of turning $100 into $10K on SafeMoon in 30 days back in 2021. See the About Us page for the full story.",
  },
  {
    q: "How do I contact you?",
    a: "DM Issy directly on Telegram at @issyflash — that's the fastest route. You can also reach us on Instagram, X and TikTok (all @issyflash).",
  },
];

function FAQItem({ item, index, isOpen, onToggle }) {
  return (
    <div
      data-testid={`faq-item-${index}`}
      className="border-b border-white/10 last:border-b-0"
    >
      <button
        onClick={onToggle}
        data-testid={`faq-toggle-${index}`}
        aria-expanded={isOpen}
        className="w-full flex items-start justify-between gap-6 py-6 md:py-7 text-left group"
      >
        <span className="font-display text-xl md:text-2xl text-white/95 group-hover:text-white transition">
          {item.q}
        </span>
        <span className={`shrink-0 w-9 h-9 rounded-full border flex items-center justify-center transition-all duration-300 ${
          isOpen
            ? "border-white/30 bg-white/10 rotate-180"
            : "border-white/15 bg-white/[0.02] group-hover:border-white/25"
        }`}>
          {isOpen ? <Minus size={16} className="text-white" /> : <Plus size={16} className="text-white/80" />}
        </span>
      </button>
      <div
        className={`grid transition-all duration-500 ease-out ${
          isOpen ? "grid-rows-[1fr] opacity-100 pb-6 md:pb-8" : "grid-rows-[0fr] opacity-0"
        }`}
      >
        <div className="overflow-hidden">
          <p className="text-white/65 text-base md:text-[17px] leading-relaxed pr-14">
            {item.a}
          </p>
        </div>
      </div>
    </div>
  );
}

export default function FAQ_Page() {
  const [openIdx, setOpenIdx] = useState(0);

  return (
    <div className="relative min-h-screen bg-black text-white overflow-x-hidden grain" data-testid="faq-page">
      <Navbar />

      <section className="relative pt-36 md:pt-44 pb-12 px-4 md:px-6">
        <div className="absolute inset-0 dot-pattern opacity-30 pointer-events-none" />
        <div className="relative z-10 max-w-3xl mx-auto text-center">
          <p className="font-mono-serif text-[10px] tracking-[0.4em] text-white/45">
            FAQ // ANSWERS
          </p>
          <h1 className="mt-4 font-display text-5xl md:text-7xl tracking-tight">
            Questions,<br/>answered.
          </h1>
          <p className="mt-5 text-white/60 max-w-xl mx-auto text-base md:text-lg">
            Everything you need to know before joining The Syndicate. Still not sure? DM @issyflash on Telegram.
          </p>
        </div>
      </section>

      <section className="relative z-10 px-4 md:px-6 pb-20">
        <div className="max-w-3xl mx-auto frame p-2 md:p-4">
          <div className="px-4 md:px-6">
            {FAQ.map((item, i) => (
              <FAQItem
                key={i}
                item={item}
                index={i}
                isOpen={openIdx === i}
                onToggle={() => setOpenIdx(openIdx === i ? -1 : i)}
              />
            ))}
          </div>
        </div>
      </section>

      <section className="relative z-10 px-4 md:px-6 pb-16">
        <div className="max-w-3xl mx-auto frame p-8 md:p-12 text-center">
          <p className="font-mono-serif text-[10px] tracking-[0.4em] text-white/45">DIRECT MESSAGE // FOUNDER</p>
          <h2 className="mt-3 font-display text-3xl md:text-4xl">Can't find your answer?</h2>
          <p className="mt-3 text-white/60 text-sm md:text-base max-w-lg mx-auto">
            DM Issy directly on Telegram — usually replies within a few hours.
          </p>
          <a
            href="https://t.me/issyflash"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="faq-telegram-btn"
            className="mt-6 inline-flex items-center gap-3 btn-shimmer text-black font-mono-serif text-[11px] tracking-[0.3em] px-8 py-4 rounded-full hover:opacity-95 transition"
          >
            <Send size={14} /> DM @ISSYFLASH ON TELEGRAM
          </a>
        </div>
      </section>

      <section className="relative z-10 px-4 md:px-6 pb-24">
        <div className="max-w-3xl mx-auto text-center frame p-10 md:p-14">
          <h2 className="font-display text-4xl md:text-5xl">Ready when you are.</h2>
          <p className="mt-4 text-white/60">Pick your market and move with the family.</p>
          <Link
            to="/pricing"
            data-testid="faq-join-btn"
            className="mt-8 inline-flex items-center gap-3 btn-shimmer text-black font-mono-serif text-xs tracking-[0.3em] px-8 py-4 rounded-full hover:opacity-95 transition"
          >
            JOIN NOW <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
